import { React, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createHttpClient, handleRemoteError } from '../../utilities/httpclient';
import { AbdsButton, AbdsInput } from '@abds/react-bindings';
import createUserSessionActivity from '../../utilities/usersessiontracking';
import { createDisplayAreaMessengerContext, DisplayAreaMessenger } from '../displayareamessenger/DisplayAreaMessenger';
import { APPLICATION_API_ENDPOINT, REGEX_EMAIL, REGEX_PHONE } from '../../utilities/constants';
import { useKeyPressEnter } from '../../customhooks/useKeyPressEnter';

import '../../styles/main.css';
import '../../styles/searchquerycontainer.css';
import '../../styles/absgtable.css';
import '../../styles/x12.css';
import '../../styles/onboarding.css';

export function OnboardingVendorDetails(props) {
    const regexEmailSplit = /[;,]+/;

    const navigate = useNavigate();

    let [messageContext, setMessageContext] = useState(createDisplayAreaMessengerContext());

    const [vendorName, setVendorName] = useState('');
    const [vendorNumber, setVendorNumber] = useState('');
    const [vendorEmails, setVendorEmails] = useState('');
    const [vendorPhone, setVendorPhone] = useState('');
    const [disableSaveButton, setDisableSaveButton] = useState(true);

    const rVendorName = useRef(null);
    const rVendorNumber = useRef(null);
    const rVendorPhone = useRef(null);
    const rSubmitButton = useRef(null);

    const onVendorNameChange = e => setVendorName(e.target.value);

    const onVendorNumberChange = e => setVendorNumber(e.target.value);

    const onVendorEmailsChange = e => setVendorEmails(e.target.value);

    const onVendorPhoneChange = e => setVendorPhone(e.target.value);

    const onClickReset = e => {
        e.preventDefault();
        resetForm();
    };

    const resetForm = () => {
        setVendorEmails('');
        setVendorName('');
        setVendorNumber('');
    };

    const onClickSave = e => {
        e.preventDefault();
        setMessageContext({});
        let newMessageContext = createDisplayAreaMessengerContext();

        if (vendorName.trim().length === 0)
            newMessageContext.error.details.push('A name is required to onboard a vendor');

        if (vendorNumber.trim().length === 0)
            newMessageContext.error.details.push('A vendor number is required to onboard a vendor');

        if (vendorPhone.trim().length === 0)
        {
            newMessageContext.error.details.push('A phone number is required to onboard a vendor');
        }
        else if (!checkPhoneFormat(vendorPhone))
        {
            newMessageContext.error.details.push('The phone number has an incorrect format');
        }

        if (vendorEmails.trim().length === 0)
        {
            newMessageContext.error.details.push('At least one (1) vendor email is required to onboard a vendor');
        }
        else if (!checkEmailFormats(vendorEmails))
        {
            newMessageContext.error.details.push('At least one (1) email has an incorrect format');
        }

        if (newMessageContext.error.details.length > 0)
        {
            newMessageContext.error.message = "One or more errors were found";
            setMessageContext(newMessageContext);
        }
        else
        {
            saveVendorDetails();
        }
    };

    const checkEmailFormats = emails => {
        const emailList = emails?.split(regexEmailSplit).map(email => email.trim());
        return emailList?.every(email => REGEX_EMAIL.test(email));
    };

    const checkPhoneFormat = phone => REGEX_PHONE.test(phone);

    const showErrorMessage = msg => {
        setMessageContext({});
        let newMessageContext = createDisplayAreaMessengerContext();
        newMessageContext.error.message = msg;
        setMessageContext(newMessageContext);
    };

    const saveVendorDetails = async () => {
        const data = {
            vendorName,
            vendorNumber,
            vendorPhone,
            vendorEmails: vendorEmails.split(regexEmailSplit).map(email => email.trim()).filter(email => email.length > 0)
        };
        const httpclient = createHttpClient();
        await httpclient.post(`${APPLICATION_API_ENDPOINT}/onboarding/initialsave`, data).then(response => {
            const oID = response?.data;
            const messageContext = createDisplayAreaMessengerContext();
            messageContext.success.message = `Vendor details saved successfully! Go <a href="/onboarding/vendordetails/${oID}">here</a> to modify this vendor's tech details.`;
            setMessageContext(messageContext);
            setVendorEmails(data.vendorEmails.join(';'));
            setDisableSaveButton(true);
        }).catch(e => (handleRemoteError(navigate, e) || showErrorMessage(e.response?.data || e.message)));;
    };

    const checkDataIntegrity = () => vendorEmails?.trim().length >= 2 && vendorName?.trim().length >= 2 && vendorEmails?.trim().length > 0 && vendorPhone?.trim().length > 0;

    useKeyPressEnter(rSubmitButton);

    useEffect(() => {
        createUserSessionActivity(window.location.pathname);
    }, []);

    useEffect(() => {
        rVendorNumber.current?.addEventListener('abdsInput', onVendorNumberChange);
        rVendorName.current?.addEventListener('abdsInput', onVendorNameChange);
        rVendorPhone.current?.addEventListener('abdsInput',onVendorPhoneChange);

        return () => {
            rVendorName.current?.removeEventListener('abdsInput', onVendorNameChange);
            rVendorNumber.current?.removeEventListener('abdsInput', onVendorNumberChange);
            rVendorPhone.current?.removeEventListener('abdsInput', onVendorPhoneChange);
        };
    }, [rVendorName, rVendorNumber, rVendorPhone]);

    useEffect(() => {
        setDisableSaveButton(!checkDataIntegrity());
    }, [vendorEmails, vendorNumber, vendorName]);

    return (
        <div>
            <h3>Onboarding: New Vendor</h3>
            <hr className="abcblue" />

            <DisplayAreaMessenger context={messageContext} />

            <h4>Vendor Details</h4>

            <div data-role="selfserviceinformation">
                <div data-role="informationitem">

                    <span className="selfservicetextboxcontainer">
                        <AbdsInput
                            type="text"
                            width="full"
                            name="vendorName"
                            required
                            ref={rVendorName}
                            value={vendorName}
                            tooltip="Enter vendor name here"
                            placeholder="Vendor Name">
                            Vendor Name
                        </AbdsInput>
                    </span>

                </div>
                <div data-role="informationitem">

                    <span className="selfservicetextboxcontainer">
                        <AbdsInput
                            type="text"
                            width="full"
                            name="vendorNumber"
                            required
                            ref={rVendorNumber}
                            value={vendorNumber}
                            tooltip="Enter vendor number here"
                            placeholder="Vendor Number">
                            Vendor Number
                        </AbdsInput>
                    </span>

                </div>
                <div data-role="informationitem">

                    <span className="selfservicetextboxcontainer">
                        <AbdsInput
                            type="text"
                            width="full"
                            name="vendorPhone"
                            required
                            ref={rVendorPhone}
                            value={vendorPhone}
                            tooltip="Enter vendor phone here"
                            placeholder="Vendor Phone">
                            Vendor Phone
                        </AbdsInput>
                    </span>

                </div>
                <div data-role="informationitem">

                    <span className="selfservicetextboxcontainer colspan2">
                        <div class="newcontrollabel"><span className="asterisk required">*</span>Vendor Emails</div>
                        <textarea name="vendorEmails" className="largetextarea" placeholder="Vendor Emails" onChange={onVendorEmailsChange} value={vendorEmails}></textarea>
                        <div class="newtooltip">For multiple emails, use a semicolon (;) or comma (,) to separate them</div>
                    </span>

                </div>
            </div>

            <hr />

            <div className="buttoncontainer">
                <AbdsButton name="cancelsettings" onClick={onClickReset} appearance="outline" palette="brand">Reset</AbdsButton>
                &nbsp;&nbsp;&nbsp;
                <AbdsButton disabled={disableSaveButton} onClick={onClickSave} ref={rSubmitButton}>Save</AbdsButton>
            </div>

        </div>
    );
}